import { Stack } from '@mui/material';
import type { FormikHelpers } from 'formik';
import { Form, Formik } from 'formik';
import { navigate } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { Flags, Schema } from 'yup';
import { object, string } from 'yup';

import RecordAttachmentForm from '@components/Attachments/RecordAttachmentForm';
import type { Curation } from '@components/DrilldownHeader';
import DrilldownItem from '@components/DrilldownItem';
import { FormikFhirDateField, FormikTextField } from '@components/FormikFields';
import { RecordSubmitButton } from '@components/RecordSubmitButton';

import { INSURANCE } from '@constants/routes';

import { useCreateInsurance, useUpdateInsurance } from '@hooks/api/insurance';
import useFhirRecordForm from '@hooks/useFhirRecordForm';
import { useFormat } from '@hooks/useFormat';
import { useIsMobile } from '@hooks/useIsMobile';
import { useSnackbar } from '@hooks/useSnackbar';

import { HorizontalRule } from '@layouts/AuthLayout';
import { ColumnLayout } from '@layouts/DrilldownStyle';

import { getCoverageTemplate } from '@models/fhir-templates/insurance';
import type {
  FormValues,
  InsurancePlan,
  InsuranceValues,
} from '@models/insurance';
import type { DrilldownProps } from '@models/record-drilldown';

import { getRecordSourceForSubmit } from '@utils/getRecordSourceForSubmit';
import { stripEmptyStrings } from '@utils/strip';
import { dateNotBeforeSchema } from '@utils/validation';

import SharingDrilldownWrapper from '@views/Records/Common/SharingDrilldownWrapper';

import DrilldownWrapper from '../Common/DrilldownWrapper';

const messages = defineMessages({
  insurance: {
    id: 'insuranceDrilldown.title',
    defaultMessage: 'Insurance',
  },
  planIdentifier: {
    id: 'insuranceDrilldown.planIdentifier',
    defaultMessage: 'Plan Identifier',
  },
  enrollmentCode: {
    id: 'insuranceDrilldown.enrollmentCode',
    defaultMessage: 'Enrollment Code',
  },
  planNumber: {
    id: 'insuranceDrilldown.planNumber',
    defaultMessage: 'Plan Number',
  },
  provider: {
    id: 'insuranceDrilldown.provider',
    defaultMessage: 'Provider',
  },
  additionalInformation: {
    id: 'insuranceDrilldown.additionalInformation',
    defaultMessage: 'Additional Information',
  },
  issued: {
    id: 'insuranceDrilldown.issued',
    defaultMessage: 'Issued',
  },
  expires: {
    id: 'insuranceDrilldown.expires',
    defaultMessage: 'Expires',
  },
  expiryBeforeIssue: {
    id: 'GLOBAL.dateValidation.expiryBeforeIssue',
    defaultMessage: 'Expiry date cannot be before issue date',
  },
  country: {
    id: 'GLOBAL.country',
    defaultMessage: 'Country',
  },
  error: {
    id: 'GLOBAL.error',
    defaultMessage: 'An error occurred. Please try again later.',
  },
  save: {
    id: 'GLOBAL.save',
    defaultMessage: 'Save',
  },
  required: {
    id: 'GLOBAL.required',
    defaultMessage: 'This field is required',
  },
});

interface InsuranceDrilldownProps extends DrilldownProps<InsurancePlan> {
  /**
   * When true, suppresses the default redirect to the INSURANCE route
   * after successfully creating a new insurance record.
   * - Default: false (keeps existing behavior in normal routes)
   * - In auto-curation flow, this should be set to true.
   */
  disableNavigateOnCreate?: boolean;
}

const InsuranceDrilldown: React.FC<InsuranceDrilldownProps> = ({
  data,
  loading: baseLoading,
  isSharing = false,
  onSubmit,
  isCreating,
  isEditing,
  patientId: patient_id,
  isCurator,
  disableNavigateOnCreate = false,
}) => {
  const { formatMessage } = useIntl();
  const { formatDateLabel } = useFormat();
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useIsMobile('sm');

  const [createInsurance, createInsuranceLoading] = useCreateInsurance();
  const [updateInsurance, updateInsuranceLoading] = useUpdateInsurance();
  const {
    formikHelpers: { baseFhirValidationSchema, initialFhirFormValues },
    loading: recordStatusAndSourceLoading,
    handleRecordFilesSourcePublishing,
  } = useFhirRecordForm();
  const isModifying = isCreating || isEditing;

  const loading =
    baseLoading ||
    createInsuranceLoading ||
    updateInsuranceLoading ||
    recordStatusAndSourceLoading;

  const initialValues: FormValues = {
    ...initialFhirFormValues,
    additionalInformation: data?.additionalInformation ?? '',
    country: data?.country ?? '',
    enrollmentCode: data?.enrollmentCode ?? '',
    expirationDate: data?.expirationDate ?? '',
    issueDate: data?.issueDate ?? '',
    planIdentifier: data?.planIdentifier ?? '',
    planNumber: data?.planNumber ?? '',
    provider: data?.provider ?? '',
  };

  const insuranceValidationSchema: Schema<Partial<InsuranceValues>> =
    object().shape({
      additionalInformation: string(),
      country: string(),
      enrollmentCode: string(),
      expirationDate: dateNotBeforeSchema(
        'issueDate',
        formatMessage(messages.expiryBeforeIssue)
      ),
      issueDate: string(),
      planIdentifier: string(),
      planNumber: string(),
      provider: string().required(formatMessage(messages.required)),
    });

  const validationSchema: Schema<
    Partial<FormValues>,
    any,
    any,
    Flags
  > = insuranceValidationSchema.concat(baseFhirValidationSchema);

  async function handleSubmit(
    values: FormValues,
    { validateForm }: FormikHelpers<FormValues>
  ) {
    if (!patient_id) {
      return;
    }
    values = stripEmptyStrings(values);
    await validateForm(stripEmptyStrings(values));

    let coverage: InsurancePlan | null | undefined;
    try {
      if (data?.id) {
        const { data: updatedInsurance } = await updateInsurance({
          input: {
            id: data.id,
            coverage: data.getModelInput(values),
          },
        });
        coverage = updatedInsurance;
      } else {
        const { data } = await createInsurance({
          input: {
            ...getCoverageTemplate(patient_id, values),
          },
        });
        coverage = data;
      }
    } catch {
      enqueueSnackbar({
        variant: 'error',
        message: formatMessage(messages.error),
      });
    } finally {
      await handleRecordFilesSourcePublishing({
        resource: coverage?.getResourceId(),
        files: values.files,
        toPublish: values.toPublish,
        recordSource: getRecordSourceForSubmit({
          isCurator,
          isCreating,
          initialRecordSource: initialValues.recordSource,
          currentRecordSource: values.recordSource,
        }),
      });
      // Existing behavior preserved by default.
      // In auto-curation flow, disableNavigateOnCreate=true will skip this.
      if (coverage && isCreating && !disableNavigateOnCreate) {
        navigate(`${INSURANCE}${coverage.id}`);
      }
      onSubmit?.();
    }
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ setFieldValue, values }) => {
        const children = (
          <>
            <ColumnLayout>
              <DrilldownItem
                label={formatMessage(messages.planNumber)}
                name="planNumber"
                value={data?.planNumber}
              />
              <DrilldownItem
                label={formatMessage(messages.planIdentifier)}
                name="planIdentifier"
                value={data?.planIdentifier}
              />
            </ColumnLayout>

            <ColumnLayout>
              <DrilldownItem
                label={formatMessage(messages.enrollmentCode)}
                name="enrollmentCode"
                value={data?.enrollmentCode}
              />
            </ColumnLayout>

            <ColumnLayout>
              <DrilldownItem
                label={formatMessage(messages.additionalInformation)}
                name="additionalInformation"
                type="text-area"
                value={data?.additionalInformation}
              />
            </ColumnLayout>

            <RecordAttachmentForm
              isCreating={isCreating}
              onFileChange={(files: DroppedFile[]) => {
                setFieldValue('files', files);
              }}
              patientId={patient_id}
              record={{
                attachments: data?.attachments,
                resourceId: data?.getResourceId(),
              }}
            />

            {isModifying && (
              <>
                <HorizontalRule />
                <RecordSubmitButton />
              </>
            )}
          </>
        );
        const drilldownProps: Curation.DrilldownHeaderProps = {
          items: [
            {
              editingComponent: (
                <FormikTextField
                  name="country"
                  placeholder={formatMessage(messages.country)}
                />
              ),
              title: data?.country,
              iconName: 'location-dot',
            },
          ],
          loading,
          resource: data,
          selfReported: data?.selfReported,
          subtitle: isModifying ? (
            <Stack direction={isMobile ? 'column' : 'row'} columnGap={2}>
              <FormikFhirDateField
                formats={['date']}
                label={formatMessage(messages.issued)}
                name="issueDate"
                onChange={(value) => {
                  setFieldValue('issueDate', value);
                }}
                value={data?.issueDate}
                max={true}
              />
              <FormikFhirDateField
                formats={['date']}
                label={formatMessage(messages.expires)}
                name="expirationDate"
                onChange={(value) => {
                  setFieldValue('expirationDate', value);
                }}
                value={data?.expirationDate}
                min={values.issueDate}
              />
            </Stack>
          ) : (
            [
              formatDateLabel(messages.issued, data?.getIssueDateStr()),
              formatDateLabel(messages.expires, data?.getExpiryDateStr()),
            ]
          ),
          title: data?.provider ?? formatMessage(messages.insurance),
          titleInput: (
            <FormikTextField
              name="provider"
              placeholder={formatMessage(messages.provider)}
            />
          ),
        };

        return (
          <Form>
            {isSharing ? (
              <SharingDrilldownWrapper {...drilldownProps}>
                {children}
              </SharingDrilldownWrapper>
            ) : (
              <DrilldownWrapper
                backRoute={INSURANCE}
                notes={data?.recordNotes}
                {...drilldownProps}
              >
                {children}
              </DrilldownWrapper>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

export default InsuranceDrilldown;
